import type { ObjectTransition, ObjectTypeDefinition } from "daedalus-core";
import { CHEST, DUMMY, SHRINE, WAYPOINT } from "../editor/type-names.ts";
import { MAX_VERB_CHARS } from "../protocol/limits.ts";
import { VERB_PATTERN } from "../protocol/text.ts";

/**
 * Verbs (ADR-051, ADR-058). A verb is any well-formed name an object type's transitions declare; the
 * protocol's spelling rule bounds what may travel, and the type's transitions, read from the game contract
 * by client and map server alike, are what a verb does. The key the client offers is therefore the transition the
 * map server applies, and a verb this code has never named works as soon as a contract declares it. A type a blow
 * drives (the training dummy, ADR-058's amendment for P2.2-T7) is the exception: its transitions name the strips a
 * blow plays and are never a verb a player uses. So is a shrine (ADR-096 decision 3), whose transitions name the strip
 * an arrival pops and whose look each viewer's own page chooses. A chest's `shut` is the map server's alone to apply (ADR-091
 * decision 17 and its amendment of 2026-10-06): the map server shuts a chest when nobody has its loot window open, so
 * the verb is offered to no player on any key and is no transition a player makes, while its `open` is a player's from
 * both states. A page sends `shut` to say its loot window closed; the map server answers that itself, ahead of the
 * transitions here, which refuse it.
 */

/**
 * The verb that shuts a chest. Only the map server applies it; from a page it says the chest's loot window closed.
 */
export const CHEST_SHUT_VERB = "shut";

/**
 * The verb that opens travel at a shrine thing or a waypoint (ADR-137 decision 6, amending ADR-096 decision 3). No
 * transition declares it: the map server answers it ahead of the transitions, from inside the place's activation cells,
 * with a `travelOffer` or a `travelState` refusal, and the place's state never moves.
 */
export const TRAVEL_VERB = "travel";

/**
 * Why a verb does not move an object: its type declares no transition with that verb, or declares one but
 * not from the state the object is in.
 */
export type VerbRefusal = "verb" | "state";

/**
 * The transition a verb makes from a state, or why there is none.
 */
export type TransitionVerdict = { readonly ok: true; readonly transition: ObjectTransition } | { readonly ok: false; readonly refusal: VerbRefusal };

const NO_VERB: TransitionVerdict = Object.freeze({ ok: false, refusal: "verb" });
const WRONG_STATE: TransitionVerdict = Object.freeze({ ok: false, refusal: "state" });

/**
 * Whether a type is driven by blows rather than verbs: the combat phase sets its states, so it offers no verb and
 * refuses every verb it declares.
 *
 * @param type The object's type, or undefined when the contract does not declare it.
 * @returns True for the training dummy.
 */
export function isBlowDriven(type: ObjectTypeDefinition | undefined): boolean {
  return type?.type === DUMMY;
}

/**
 * Whether a type offers no verb and refuses every verb it declares: one a blow drives, or a shrine or a waypoint, which
 * nothing a player sends ever moves (ADR-096 decision 3, ADR-137 decision 2).
 *
 * @param type The object's type, or undefined when the contract does not declare it.
 * @returns True for the training dummy, the shrine and the waypoint.
 */
export function offersNoVerb(type: ObjectTypeDefinition | undefined): boolean {
  return isBlowDriven(type) || type?.type === SHRINE || type?.type === WAYPOINT;
}

/**
 * Whether a verb a type declares is the map server's to apply and never a player's: a chest's `shut`.
 *
 * @param type The object's type, or undefined when the contract does not declare it.
 * @param verb The verb name.
 * @returns True for `shut` on a chest.
 */
export function isServerVerb(type: ObjectTypeDefinition | undefined, verb: string): boolean {
  return type?.type === CHEST && verb === CHEST_SHUT_VERB;
}

/**
 * The verb a player can aim at an object in a state: the first transition its type declares leaving that
 * state that is a player's to use; none on a type that offers no verb.
 *
 * @param type The object's type, or undefined when the contract does not declare it.
 * @param stateId The object's current state id.
 * @returns The verb, or undefined when the object offers none now.
 */
export function verbFor(type: ObjectTypeDefinition | undefined, stateId: string): string | undefined {
  return offersNoVerb(type) ? undefined : type?.transitions.find((t) => t.from === stateId && !isServerVerb(type, t.verb))?.verb;
}

/**
 * The transition a verb makes on an object in a state. Guards (`requires`) are the map server's to evaluate and
 * are not checked here.
 *
 * @param type The object's type, or undefined when the contract does not declare it.
 * @param verb The verb name.
 * @param stateId The object's current state id.
 * @returns The transition, or the refusal: `verb` when the type declares no such verb, offers none or keeps the verb
 *   for the map server, `state` when it declares it but not from this state.
 */
export function transitionFor(type: ObjectTypeDefinition | undefined, verb: string, stateId: string): TransitionVerdict {
  if (offersNoVerb(type) || isServerVerb(type, verb)) {
    return NO_VERB;
  }
  let declared = false;
  for (const transition of type?.transitions ?? []) {
    if (transition.verb !== verb) {
      continue;
    }
    if (transition.from === stateId) {
      return { ok: true, transition };
    }
    declared = true;
  }
  return declared ? WRONG_STATE : NO_VERB;
}

/**
 * Whether a string is a well-formed verb name for the wire: the protocol's spelling rule.
 *
 * @param value Candidate.
 * @returns True when it may travel in an `interact` frame.
 */
export function isVerbName(value: string): boolean {
  return VERB_PATTERN.test(value) && value.length <= MAX_VERB_CHARS;
}

/**
 * Checks that every verb an object type declares may travel in an `interact` frame, so a client never
 * offers a verb the gateway would close the connection for.
 *
 * @param type The object type.
 * @throws Error naming the type and the verb, cut to one character past the longest name the protocol
 *   carries, when a verb breaks the spelling rule.
 */
export function assertVerbNames(type: ObjectTypeDefinition): void {
  for (const transition of type.transitions) {
    if (!isVerbName(transition.verb)) {
      throw new Error(`object type ${type.type} declares verb ${JSON.stringify(transition.verb.slice(0, MAX_VERB_CHARS + 1))}, which is not a verb name the protocol carries`);
    }
  }
}
